import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Truck, Send, Ban, Check, Pencil } from 'lucide-react';
import {
  Field, Messages, SearchSelect, Table, capabilitiesAllow, decimalProps, displayDec, errorMessage, formatWhen,
  isPositiveDecimal, money, scopeKey, shortId, statusLabel, useMktRuntime, useMktScope, useStableIds,
} from './MarketplaceShared';
import './Marketplace.css';

const enc = encodeURIComponent;
const SETTLEMENTS = [{ value: 'prepaid_card', label: 'Prepaid card (franchise)' }, { value: 'intercompany', label: 'Intercompany' }];
const KINDS = [{ value: 'franchise', label: 'Franchise' }, { value: 'supplies', label: 'Supplies' }, { value: 'manages', label: 'Manages' }];
const FULFILMENTS = [
  { value: 'ck_made', label: 'CK made (recipe)' },
  { value: 'ck_stocked', label: 'CK stocked (stock item)' },
  { value: 'vendor_dropship', label: 'Vendor drop-ship' },
];
const DROPSHIP_POLICIES = [
  { value: '', label: 'Choose policy' },
  { value: 'vendor_to_ck', label: 'Vendor invoices CK' },
  { value: 'vendor_to_buyer', label: 'Vendor invoices buyer' },
];
const ALLOCATABLE = ['paid', 'allocated', 'fulfilling'];
const STOCK_FULFILMENTS = ['ck_made', 'ck_stocked'];
const today = () => new Date().toISOString().slice(0, 10);
const asList = v => (Array.isArray(v) ? v : []);

export default function MarketplaceOperator() {
  const scope = useMktScope();
  return <OperatorWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function OperatorWorkspace({ scope }) {
  const rt = useMktRuntime(scope);
  const ids = useStableIds();
  const { active, request, busy, error, notice, setError, setNotice } = rt;
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');
  const [tab, setTab] = useState('relationships');
  const [orgNames, setOrgNames] = useState({});
  const [orgOptions, setOrgOptions] = useState([]);
  const [priceOptions, setPriceOptions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setBlocked(''); setError('');
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use CK marketplace operations.');
      const c = await request('get', '/capabilities');
      if (!active()) return;
      if (!capabilitiesAllow(c)) { setCaps(null); setBlocked('Marketplace is not enabled for this organization, or you do not have access.'); return; }
      setCaps(c);
      if (c.can_write) {
        const [orgs, lists] = await Promise.all([
          request('get', '/lookups/organizations').catch(() => []),
          request('get', '/lookups/price-lists').catch(() => []),
        ]);
        if (!active()) return;
        setOrgNames(Object.fromEntries(asList(orgs).map(o => [o.id, o.name])));
        setOrgOptions(asList(orgs).map(o => ({ value: o.id, label: o.name || o.id })));
        setPriceOptions(asList(lists).map(p => ({ value: p.price_list_id, label: `${p.customer_class || 'Price list'} v${p.version} (from ${p.effective_from})` })));
      }
    } catch (e) { if (active()) { setCaps(null); setBlocked(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [scope, request, active, setError]);
  useEffect(() => { load(); }, [load]);

  const loadOrgs = useCallback(async text => asList(await request('get', '/lookups/organizations', { params: { q: text } }))
    .map(o => ({ value: o.id, label: o.name || o.id })), [request]);

  const tabs = caps ? [
    ...(caps.can_read ? [['relationships', 'Relationships'], ['catalogue', 'Catalogue']] : []),
    ...(caps.can_operate ? [['allocation', 'Allocation']] : []),
  ] : [];
  const shared = { rt, ids, caps, scope, orgNames, orgOptions, priceOptions, loadOrgs };

  return (
    <main className="mkt-ops" data-testid="marketplace-operator">
      <header>
        <h1>CK marketplace ops</h1>
        <button type="button" title="Refresh" aria-label="Refresh CK marketplace" disabled={busy || loading} onClick={load}><RefreshCw size={18} /></button>
      </header>
      <Messages error={error} notice={notice} />
      {loading && <p role="status">Checking marketplace access...</p>}
      {!loading && blocked && <div className="mkt-error" role="alert">{blocked}</div>}
      {!loading && caps && <>
        <div className="mkt-tabs" role="tablist" aria-label="CK marketplace workflows">
          {tabs.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} disabled={busy} onClick={() => { setTab(key); setNotice(''); setError(''); }}>{label}</button>
          ))}
        </div>
        {tab === 'relationships' && caps.can_read && <RelationshipsTab {...shared} />}
        {tab === 'catalogue' && caps.can_read && <CatalogueTab {...shared} />}
        {tab === 'allocation' && caps.can_operate && <AllocationTab {...shared} />}
      </>}
    </main>
  );
}

// --- Relationships -----------------------------------------------------------

function RelationshipsTab({ rt, ids, caps, orgNames, orgOptions, priceOptions, loadOrgs }) {
  const { active, request, run, busy, setError } = rt;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [priceList, setPriceList] = useState(null);
  const [settlement, setSettlement] = useState('prepaid_card');
  const [kind, setKind] = useState('franchise');
  const [currency, setCurrency] = useState('GBP');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [notes, setNotes] = useState('');
  const [policies, setPolicies] = useState({});
  const me = caps.organization_id;
  const nameOf = id => orgNames[id] || id;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('get', '/relationships', { params: { side: 'any' } });
      if (active()) setRows(asList(data));
    } catch (e) { if (active()) { setRows([]); setError(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [request, active, setError]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    const code = currency.trim().toUpperCase();
    if (!customer) { setError('Choose a customer organization.'); return; }
    if (!priceList) { setError('Choose the price list this customer buys from.'); return; }
    if (!/^[A-Z]{3}$/.test(code)) { setError('Currency must be a 3-letter code such as GBP.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) { setError('Choose an effective-from date.'); return; }
    const terms = { kind, customer_org_id: customer.value, settlement_terms: settlement, price_list_id: priceList.value, effective_from: `${effectiveFrom}T00:00:00+00:00`, currency: code, ...(notes.trim() ? { notes: notes.trim() } : {}) };
    const fingerprint = ['relationship', terms];
    const r = await run(() => request('post', '/relationships', { body: { relationship_id: ids.idFor(fingerprint), ...terms } }), 'Relationship proposed. It stays pending until the customer accepts.');
    if (r?.ok && active()) { ids.release(fingerprint); setCustomer(null); setPriceList(null); setNotes(''); await reload(); }
  };
  const act = async (rel, action) => {
    const fingerprint = ['relationship-action', action, rel.relationship_id];
    const body = action === 'accept' ? { idempotency_key: ids.idFor(fingerprint) } : {};
    const r = await run(() => request('post', `/relationships/${enc(rel.relationship_id)}/${action}`, { body }), action === 'accept' ? 'Relationship accepted.' : 'Relationship suspended.');
    if (r?.ok && active()) { ids.release(fingerprint); await reload(); }
  };
  const savePolicy = async rel => {
    const value = policies[rel.relationship_id];
    if (!value) { setError('Choose a drop-ship invoice policy.'); return; }
    const r = await run(() => request('patch', `/relationships/${enc(rel.relationship_id)}/dropship-policy`, { body: { dropship_invoice_policy: value } }), 'Drop-ship policy saved.');
    if (r?.ok && active()) await reload();
  };

  return (
    <section role="tabpanel" aria-label="Relationships">
      {caps.can_write && (
        <details>
          <summary>Propose relationship</summary>
          <form className="mkt-form" aria-label="Propose relationship" onSubmit={e => { e.preventDefault(); create(); }}>
            <fieldset disabled={busy}>
              <div className="mkt-fields">
                <SearchSelect label="Customer organization" value={customer} onChange={setCustomer} options={orgOptions} loadOptions={loadOrgs} />
                <SearchSelect label="Price list" value={priceList} onChange={setPriceList} options={priceOptions} emptyText="No published price lists at this site" />
                <Field label="Relationship kind" value={kind} onChange={e => setKind(e.target.value)} options={KINDS} />
                <Field label="Settlement terms" value={settlement} onChange={e => setSettlement(e.target.value)} options={SETTLEMENTS} />
                <Field label="Currency" type="text" maxLength={3} value={currency} onChange={e => setCurrency(e.target.value)} />
                <Field label="Effective from" type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} />
                <Field label="Notes" type="text" value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
              <button type="submit" className="mkt-primary">Propose relationship</button>
            </fieldset>
          </form>
        </details>
      )}
      {loading ? <p role="status">Loading relationships...</p> : (
        <Table headers={['Counterparty', 'Your role', 'Kind', 'Status', 'Settlement', 'Drop-ship invoicing', 'Effective from', 'Actions']} label="Relationships" empty={!rows.length} emptyText="No relationships yet.">
          {rows.map(rel => {
            const supplier = rel.supplier_org_id === me;
            const customerSide = rel.customer_org_id === me;
            const other = supplier ? rel.customer_org_id : rel.supplier_org_id;
            const policy = rel.dropship_invoice_policy || 'unconfigured';
            return (
              <tr key={rel.relationship_id}>
                <td>{nameOf(other)}</td>
                <td>{supplier ? 'Supplier' : 'Customer'}</td>
                <td>{statusLabel(rel.kind)}</td>
                <td>{statusLabel(rel.status)}</td>
                <td>{statusLabel(rel.settlement_terms)}</td>
                <td>
                  {statusLabel(policy)}
                  {supplier && caps.can_write && rel.status === 'active' && (
                    <div className="mkt-actions">
                      <select aria-label={`Drop-ship policy for ${nameOf(other)}`} value={policies[rel.relationship_id] || ''} disabled={busy}
                        onChange={e => setPolicies({ ...policies, [rel.relationship_id]: e.target.value })}>
                        {DROPSHIP_POLICIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <button type="button" aria-label={`Save drop-ship policy for ${nameOf(other)}`} disabled={busy} onClick={() => savePolicy(rel)}>Save</button>
                    </div>
                  )}
                </td>
                <td>{String(rel.effective_from || '').slice(0, 10)}</td>
                <td>
                  <div className="mkt-actions">
                    {customerSide && rel.status === 'pending_acceptance' && caps.can_write && (
                      <button type="button" aria-label={`Accept relationship with ${nameOf(other)}`} disabled={busy} onClick={() => act(rel, 'accept')}><Check size={16} />Accept</button>
                    )}
                    {supplier && rel.status === 'active' && caps.can_write && (
                      <button type="button" aria-label={`Suspend relationship with ${nameOf(other)}`} disabled={busy} onClick={() => act(rel, 'suspend')}><Ban size={16} />Suspend</button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>
      )}
    </section>
  );
}

// --- Catalogue ---------------------------------------------------------------

const blankItem = { item_id: '', display_name: '', category: '', fulfilment: 'ck_made', pack_unit: '', pack_size: '1', source: null, vendor_sku: '' };

function CatalogueTab({ rt, ids, caps, scope }) {
  const { active, request, run, busy, setError, setNotice } = rt;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState(blankItem);
  const [recipes, setRecipes] = useState([]);
  const [stock, setStock] = useState([]);
  const [vendors, setVendors] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('get', '/catalogue', { params: filter ? { status: filter } : undefined });
      if (active()) setRows(asList(data));
    } catch (e) { if (active()) { setRows([]); setError(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [request, active, setError, filter]);
  useEffect(() => { reload(); }, [reload]);

  const stockOptions = useCallback(async text => {
    const data = await request('get', '/api/inventory/stock', { external: true, params: text ? { q: text } : undefined });
    const seen = new Set();
    return asList(data).filter(r => r.ingredient_id && !seen.has(r.ingredient_id) && seen.add(r.ingredient_id))
      .map(r => ({ value: r.ingredient_id, label: `${r.name || r.ingredient_id}${r.unit ? ` (${r.unit})` : ''}` }));
  }, [request]);
  const vendorOptions = useCallback(async text => asList(await request('get', '/api/vendors', { external: true, params: text ? { search: text } : undefined }))
    .map(v => ({ value: v.id, label: v.name || v.id })), [request]);

  useEffect(() => {
    if (!caps.can_write) return;
    let cancelled = false;
    (async () => {
      try {
        if (form.fulfilment === 'ck_made' && !recipes.length) {
          const data = await request('get', '/api/costing/recipes', { external: true });
          if (!cancelled && active()) setRecipes(asList(data).filter(r => r.id).map(r => ({ value: r.id, label: r.name || r.id })));
        } else if (form.fulfilment === 'ck_stocked' && !stock.length) {
          const data = await stockOptions('');
          if (!cancelled && active()) setStock(data);
        } else if (form.fulfilment === 'vendor_dropship' && !vendors.length) {
          const data = await vendorOptions('');
          if (!cancelled && active()) setVendors(data);
        }
      } catch { /* selector stays empty; typing still searches server-side */ }
    })();
    return () => { cancelled = true; };
  }, [form.fulfilment, caps.can_write, recipes.length, stock.length, vendors.length, request, active, stockOptions, vendorOptions]);

  const pool = { ck_made: recipes, ck_stocked: stock, vendor_dropship: vendors }[form.fulfilment];
  const loader = { ck_stocked: stockOptions, vendor_dropship: vendorOptions }[form.fulfilment];
  const sourceLabel = { ck_made: 'Recipe', ck_stocked: 'Stock item', vendor_dropship: 'Vendor' }[form.fulfilment];
  const set = patch => setForm(f => ({ ...f, ...patch }));

  const edit = item => {
    const id = item.recipe_id || item.ingredient_id || item.stock_item_id || item.vendor_id;
    const known = [...recipes, ...stock, ...vendors].find(o => o.value === id);
    setForm({
      item_id: item.item_id, display_name: item.display_name, category: item.category, fulfilment: item.fulfilment,
      pack_unit: item.pack_unit, pack_size: String(item.pack_size), vendor_sku: item.vendor_sku || '',
      source: id ? { value: id, label: known?.label || id } : null,
    });
    setNotice('');
  };

  const save = async () => {
    const f = form;
    if (!scope.locationId) { setError('Select the CK site that publishes this item.'); return; }
    if (!f.display_name.trim() || !f.category.trim() || !f.pack_unit.trim()) { setError('Name, category and pack unit are required.'); return; }
    if (!isPositiveDecimal(f.pack_size)) { setError('Pack size must be greater than zero.'); return; }
    if (!f.source) { setError(`Choose a ${sourceLabel.toLowerCase()}.`); return; }
    if (f.fulfilment === 'vendor_dropship' && !f.vendor_sku.trim()) { setError('Vendor SKU is required for drop-ship items.'); return; }
    const source = { ck_made: { recipe_id: f.source.value }, ck_stocked: { ingredient_id: f.source.value }, vendor_dropship: { vendor_id: f.source.value, vendor_sku: f.vendor_sku.trim() } }[f.fulfilment];
    const fields = { display_name: f.display_name.trim(), category: f.category.trim(), fulfilment: f.fulfilment, pack_unit: f.pack_unit.trim(), pack_size: f.pack_size.trim(), ...source };
    const fingerprint = ['catalogue', f.item_id, fields];
    const item_id = f.item_id || ids.idFor(fingerprint);
    const r = await run(() => request('post', '/catalogue', { body: { item_id, ...fields } }), 'Draft saved. Publish it to make it visible to buyers.');
    if (r?.ok && active()) { ids.release(fingerprint); setForm(blankItem); await reload(); }
  };
  const publish = async item => {
    const r = await run(() => request('post', `/catalogue/${enc(item.item_id)}/publish`, { body: {} }), `${item.display_name} published.`);
    if (r?.ok && active()) await reload();
  };

  return (
    <section role="tabpanel" aria-label="Catalogue">
      {!scope.locationId && <p className="mkt-hint" role="status">Select a CK site to create or publish catalogue items.</p>}
      {caps.can_write && (
        <details open={Boolean(form.item_id)}>
          <summary>{form.item_id ? 'Edit draft item' : 'New catalogue item'}</summary>
          <form className="mkt-form" aria-label="Catalogue item" onSubmit={e => { e.preventDefault(); save(); }}>
            <fieldset disabled={busy || !scope.locationId}>
              <div className="mkt-fields">
                <Field label="Display name" type="text" value={form.display_name} onChange={e => set({ display_name: e.target.value })} />
                <Field label="Item category" type="text" value={form.category} onChange={e => set({ category: e.target.value })} />
                <Field label="Fulfilment" value={form.fulfilment} onChange={e => set({ fulfilment: e.target.value, source: null })} options={FULFILMENTS} />
                <SearchSelect key={form.fulfilment} label={sourceLabel} value={form.source} onChange={source => set({ source })} options={pool} loadOptions={loader} />
                {form.fulfilment === 'vendor_dropship' && <Field label="Vendor SKU" type="text" value={form.vendor_sku} onChange={e => set({ vendor_sku: e.target.value })} />}
                <Field label="Pack unit" type="text" value={form.pack_unit} onChange={e => set({ pack_unit: e.target.value })} />
                <Field label="Pack size" {...decimalProps} value={form.pack_size} onChange={e => set({ pack_size: e.target.value })} />
              </div>
              <div className="mkt-actions">
                <button type="submit" className="mkt-primary">Save draft</button>
                {form.item_id && <button type="button" onClick={() => setForm(blankItem)}>Cancel edit</button>}
              </div>
            </fieldset>
          </form>
        </details>
      )}
      <div className="mkt-filters">
        <Field label="Status filter" value={filter} onChange={e => setFilter(e.target.value)} options={[{ value: '', label: 'All statuses' }, 'draft', 'published', 'unavailable']} />
      </div>
      {loading ? <p role="status">Loading catalogue...</p> : (
        <Table headers={['Item', 'Category', 'Fulfilment', 'Pack', 'Status', 'Actions']} label="Catalogue items" empty={!rows.length} emptyText="No catalogue items.">
          {rows.map(item => (
            <tr key={item.item_id}>
              <td>{item.display_name}</td>
              <td>{item.category}</td>
              <td>{statusLabel(item.fulfilment)}</td>
              <td>{displayDec(item.pack_size, 0)} {item.pack_unit}</td>
              <td>{statusLabel(item.status)}</td>
              <td>
                <div className="mkt-actions">
                  {caps.can_write && item.status !== 'published' && <button type="button" aria-label={`Edit ${item.display_name}`} disabled={busy} onClick={() => edit(item)}><Pencil size={16} />Edit</button>}
                  {caps.can_write && item.status === 'draft' && <button type="button" aria-label={`Publish ${item.display_name}`} disabled={busy} onClick={() => publish(item)}><Send size={16} />Publish</button>}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

// --- Allocation, shipping, fulfilments -----------------------------------------

function AllocationTab({ rt, ids, orgNames }) {
  const { active, request, run, busy, setError } = rt;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [lots, setLots] = useState([]);
  const [picks, setPicks] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('get', '/orders', { params: { side: 'supplier' } });
      if (active()) setOrders(asList(data).filter(o => ALLOCATABLE.includes(o.status)));
    } catch (e) { if (active()) { setOrders([]); setError(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [request, active, setError]);
  useEffect(() => { reload(); }, [reload]);

  const open = useCallback(async orderId => {
    const r = await run(() => request('get', `/orders/${enc(orderId)}`));
    if (!r?.ok || !active()) return;
    setDetail(r.result); setPicks({});
    const needsLots = asList(r.result.order?.lines).some(l => STOCK_FULFILMENTS.includes(l.fulfilment)
      && !asList(r.result.allocations).some(a => a.line_item_id === l.item_id));
    if (needsLots) {
      try {
        const data = await request('get', '/api/ck-ops/lots', { external: true });
        if (active()) setLots(asList(data));
      } catch { setLots([]); }
    }
  }, [run, request, active]);
  const refreshDetail = async orderId => {
    try {
      const d = await request('get', `/orders/${enc(orderId)}`);
      if (active()) { setDetail(d); setPicks({}); }
    } catch (e) { if (active()) setError(errorMessage(e)); }
    await reload();
  };

  const order = detail?.order;
  const allocations = asList(detail?.allocations);
  const fulfilments = asList(detail?.fulfilments);
  const allocatedIds = new Set(allocations.map(a => a.line_item_id));
  const openLines = asList(order?.lines).filter(l => !allocatedIds.has(l.item_id));
  const lotOptions = Object.fromEntries(openLines.map(line => {
    const sources = [line.recipe_id, line.stock_item_id, line.ingredient_id].filter(Boolean);
    return [line.item_id, lots
      .filter(l => sources.includes(l.item_id) && l.status === 'available' && isPositiveDecimal(String(l.remaining_qty)))
      .map(l => ({ value: l.lot_id, label: `${l.lot_id} - ${displayDec(String(l.remaining_qty), 0)} ${l.uom || ''}${l.expiry ? ` - exp ${l.expiry}` : ''}` }))];
  }));
  const picked = line => picks[line.item_id] || { checked: true, lot: null };

  const allocate = async () => {
    const specs = openLines.filter(l => picked(l).checked)
      .map(l => ({ item_id: l.item_id, ...(picked(l).lot && STOCK_FULFILMENTS.includes(l.fulfilment) ? { lot_id: picked(l).lot.value } : {}) }));
    if (!specs.length) { setError('Select at least one line to allocate.'); return; }
    const fingerprint = ['allocate', order.order_id, specs];
    const r = await run(() => request('post', `/orders/${enc(order.order_id)}/allocate`, { body: { command_id: ids.idFor(fingerprint), lines: specs } }), 'Lines allocated.');
    if (r?.ok && active()) { ids.release(fingerprint); await refreshDetail(order.order_id); }
  };
  const ship = async alloc => {
    const fingerprint = ['ship', order.order_id, alloc.allocation_id];
    const r = await run(() => request('post', `/orders/${enc(order.order_id)}/ship`, { body: { command_id: ids.idFor(fingerprint), allocation_id: alloc.allocation_id } }), 'Shipment recorded.');
    if (r?.ok && active()) { ids.release(fingerprint); await refreshDetail(order.order_id); }
  };
  const lineName = id => asList(order?.lines).find(l => l.item_id === id)?.display_name || 'Item';

  return (
    <section role="tabpanel" aria-label="Allocation">
      {loading ? <p role="status">Loading orders...</p> : (
        <Table headers={['Order', 'Customer', 'Status', 'Placed', 'Total', 'Open']} label="Orders to fulfil" empty={!orders.length} emptyText="No paid orders awaiting fulfilment.">
          {orders.map(o => (
            <tr key={o.order_id}>
              <td>{shortId(o.order_id)}</td>
              <td>{orgNames[o.customer_org_id] || o.customer_org_id}</td>
              <td>{statusLabel(o.status)}</td>
              <td>{formatWhen(o.created_at)}</td>
              <td className="mkt-number">{money(o.grand_total, o.currency)}</td>
              <td><button type="button" aria-label={`Open order ${shortId(o.order_id)}`} disabled={busy} onClick={() => open(o.order_id)}>Open</button></td>
            </tr>
          ))}
        </Table>
      )}
      {order && (
        <section className="mkt-detail" aria-label={`Order ${shortId(order.order_id)} fulfilment`}>
          <h2>Order {shortId(order.order_id)} <span className="mkt-hint">{statusLabel(order.status)}</span></h2>
          {order.status === 'paid' && openLines.length > 0 && (
            <form className="mkt-form" aria-label="Allocate lines" onSubmit={e => { e.preventDefault(); allocate(); }}>
              <h3>Allocate lines</h3>
              <fieldset disabled={busy}>
                {openLines.map(line => {
                  const stock = STOCK_FULFILMENTS.includes(line.fulfilment);
                  return (
                    <div className="mkt-line" key={line.item_id}>
                      <label className="mkt-check">
                        <input type="checkbox" aria-label={`Allocate ${line.display_name}`} checked={picked(line).checked}
                          onChange={e => setPicks({ ...picks, [line.item_id]: { ...picked(line), checked: e.target.checked } })} />
                        <span>{line.display_name} - {displayDec(line.qty, 0)} x {displayDec(line.pack_size, 0)} {line.pack_unit} ({statusLabel(line.fulfilment)})</span>
                      </label>
                      {stock
                        ? <SearchSelect label={`Lot for ${line.display_name}`} value={picked(line).lot} options={lotOptions[line.item_id] || []}
                          onChange={lot => setPicks({ ...picks, [line.item_id]: { ...picked(line), lot } })} emptyText="No matching lots; leave empty for automatic FEFO" />
                        : <span className="mkt-hint">Drafts a purchase order with the vendor</span>}
                    </div>
                  );
                })}
                <p className="mkt-hint">Leave the lot empty to use the earliest-expiry lot that holds the whole line.</p>
                <button type="submit" className="mkt-primary">Allocate selected lines</button>
              </fieldset>
            </form>
          )}
          {allocations.length > 0 && (
            <Table headers={['Line', 'Fulfilment', 'Status', 'Quantity', 'Source', 'Action']} label="Allocations">
              {allocations.map(a => (
                <tr key={a.allocation_id}>
                  <td>{lineName(a.line_item_id)}</td>
                  <td>{statusLabel(a.fulfilment)}</td>
                  <td>{statusLabel(a.status)}</td>
                  <td className="mkt-number">{displayDec(String(a.base_qty ?? a.qty), 0)} {a.uom || ''}</td>
                  <td>{a.reserved_lot_id || (a.purchase_order_id ? `PO ${shortId(a.purchase_order_id)}` : '')}</td>
                  <td>
                    {a.status === 'reserved' && STOCK_FULFILMENTS.includes(a.fulfilment) && a.reservation_kind !== 'transfer_claim' && (
                      <button type="button" aria-label={`Ship ${lineName(a.line_item_id)}`} disabled={busy} onClick={() => ship(a)}><Truck size={16} />Ship</button>
                    )}
                    {a.reservation_kind === 'transfer_claim' && a.status === 'reserved' && <span className="mkt-hint">Ships via CK transfer order</span>}
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <h3>Fulfilments</h3>
          <Table headers={['Item', 'Shipped', 'Received', 'Status', 'Shipped at']} label="Fulfilments" empty={!fulfilments.length} emptyText="Nothing shipped yet.">
            {fulfilments.map(f => (
              <tr key={f.fulfilment_id}>
                <td>{f.display_name || lineName(f.item_id)}</td>
                <td className="mkt-number">{displayDec(String(f.qty_shipped), 0)} {f.uom}</td>
                <td className="mkt-number">{f.qty_received == null ? 'Pending' : `${displayDec(String(f.qty_received), 0)} ${f.uom || ''}`}</td>
                <td>{statusLabel(f.status)}{f.loss_reason ? `: ${f.loss_reason}` : ''}</td>
                <td>{formatWhen(f.shipped_at)}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}
    </section>
  );
}
